import { useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { Link } from 'react-router-dom';
import Button from '@mui/material/Button';
import useMediaQuery from '@mui/material/useMediaQuery';

// Icons
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import InsightsIcon from '@mui/icons-material/Insights';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SecurityIcon from '@mui/icons-material/Security';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SpeedIcon from '@mui/icons-material/Speed';
import GroupsIcon from '@mui/icons-material/Groups';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import WarningIcon from '@mui/icons-material/Warning';

// Images
import computerUser from 'assets/images/photos/computer-user.jpg';
import dashboard from 'assets/images/platform/dashboard.png';
import userReports from 'assets/images/platform/reports_user_principal2.png';
import fullDashboard from 'assets/images/platform/TrawlAI-MS365-Dashboard-FullScreen.png';

export default function ManagedPartners() {
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

    useEffect(() => {
        document.title = 'PREVAIL for Managed Service Providers';
        window.scrollTo(0, 0);
    });

    return (
        <>
            {/* ─── HERO ─── */}
            <Box
                width="100%"
                sx={{
                    minHeight: isMobile ? '100vh' : '72vh',
                    background: 'linear-gradient(160deg, #000000 0%, #1a0000 50%, #242331 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={2} p={3} mt={8} justifyContent="center" alignItems="center">
                    <Grid item xs={12} md={7} p={2}>
                        <Typography
                            sx={{ typography: { xs: 'h4', md: 'h2' }, mb: 2 }}
                            fontWeight="bold"
                            color="grey.100"
                        >
                            Protect Every Client.<br />From One Platform.
                        </Typography>
                        <Typography
                            sx={{ typography: { xs: 'h6', md: 'h5' }, mb: 1 }}
                            color="prevailRed.main"
                            fontWeight="bold"
                        >
                            Multi-Tenant AI Security Built for MSPs.
                        </Typography>
                        <Typography
                            variant="h6"
                            color="grey.400"
                            sx={{ mb: 4, mt: 2, lineHeight: 1.8 }}
                        >
                            Add intelligent, automated threat detection across all your clients' Microsoft 365
                            environments — without adding headcount. PREVAIL detects active breaches in
                            under 1 minute and alerts you before damage is done.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Link to="https://app.prevailcyber.com/sign-up" target="_blank">
                                <Button
                                    variant="contained"
                                    color="prevailRed"
                                    size="large"
                                    sx={{ pl: 5, pr: 5, py: 1.5 }}
                                >
                                    <Typography sx={{ fontSize: '1.1em' }} color="grey.100">
                                        Start 30-Day Free Trial
                                    </Typography>
                                </Button>
                            </Link>
                            <Link to="/contact-us">
                                <Button
                                    variant="outlined"
                                    size="large"
                                    sx={{ pl: 4, pr: 4, py: 1.5, borderColor: 'grey.600', color: 'grey.300' }}
                                >
                                    <Typography sx={{ fontSize: '1.1em' }}>Schedule a Demo</Typography>
                                </Button>
                            </Link>
                        </Box>
                    </Grid>
                  
                </Grid>
            </Box>

            {/* ─── STATS BAR ─── */}
            <Box sx={{ background: '#6b0000', py: 4 }}>
                <Grid container maxWidth="xl" spacing={2} justifyContent="center" alignItems="center"
                    textAlign="center" sx={{ mx: 'auto' }}>
                    {[
                        { stat: '< 1 Min', label: 'Threat detection time per tenant' },
                        { stat: '< 3 Min', label: 'Time to add a new client tenant' },
                        { stat: '30%', label: 'Partner discount on retail pricing' },
                        { stat: '21%', label: 'Shorter breach lifecycle with MSSP' },
                    ].map(({ stat, label }) => (
                        <Grid item size={{ xs: 6, md: 3 }} key={stat}>
                            <Typography variant="h4" color="grey.100" fontWeight="bold">{stat}</Typography>
                            <Typography variant="body2" color="grey.400" mt={0.5}>{label}</Typography>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* ─── THE MSP OPPORTUNITY ─── */}
            <Box
                sx={{
                    background: '#242331',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={2} justifyContent="center">
                    <Grid item xs={12} textAlign="center" mb={4}>
                        <Typography variant="h3" color="grey.100" fontWeight="bold">
                            Your Clients Are Under Constant Attack
                        </Typography>
                        <Typography variant="h6" color="grey.400" mt={2} sx={{ maxWidth: '720px', mx: 'auto' }}>
                            Microsoft 365 is the most targeted platform in the world for credential theft,
                            business email compromise, and ransomware. Your clients rely on you to keep
                            them protected — PREVAIL makes that possible at scale.
                        </Typography>
                    </Grid>

                    {[
                        {
                            icon: <SpeedIcon sx={{ fontSize: 48 }} />,
                            title: 'Detect Before Damage',
                            description:
                                'Most breaches go undetected for months. PREVAIL\'s behavioral AI identifies active threats in under 1 minute, giving your team time to respond before clients lose data or face ransomware.',
                        },
                        {
                            icon: <GroupsIcon sx={{ fontSize: 48 }} />,
                            title: 'Scale Without Headcount',
                            description:
                                'Monitoring dozens of tenants manually is impossible. PREVAIL\'s AI does the analysis, filtering noise from real threats, so your team only acts when action is actually required.',
                        },
                        {
                            icon: <MonetizationOnIcon sx={{ fontSize: 48 }} />,
                            title: 'Add a Security Revenue Stream',
                            description:
                                'Bundle PREVAIL into your managed security offering. Our partner pricing gives you up to 30% off retail, so you can deliver real value while growing your margins.',
                        },
                    ].map(({ icon, title, description }) => (
                        <Grid item xs={12} md={4} key={title} p={1}>
                            <Card
                                sx={{
                                    background: '#1a1929',
                                    border: '1px solid rgba(217,0,0,0.35)',
                                    borderRadius: 2,
                                    height: '100%',
                                }}
                            >
                                <CardContent sx={{ p: 4 }}>
                                    <Typography color="prevailRed.main" mb={2}>{icon}</Typography>
                                    <Typography variant="h5" color="grey.100" fontWeight="bold" mb={2}>
                                        {title}
                                    </Typography>
                                    <Typography variant="body2" color="grey.400" lineHeight={1.8}>
                                        {description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* ─── MULTI-TENANT PLATFORM ─── */}
            <Box
                sx={{
                    background: '#f8f9fa',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={6} justifyContent="center" alignItems="center">
                    <Grid item xs={12} md={6}>
                        <Typography
                            variant="overline"
                            color="prevailRed.main"
                            fontWeight="bold"
                            sx={{ letterSpacing: 3, fontSize: '0.9em' }}
                        >
                            MULTI-TENANT MANAGEMENT
                        </Typography>
                        <Typography variant="h3" color="grey.800" fontWeight="bold" mt={1} mb={3}>
                            All Your Clients.<br />One Dashboard.
                        </Typography>
                        <Typography variant="body1" color="grey.700" mb={3} sx={{ fontSize: '1.1em', lineHeight: 1.8 }}>
                            PREVAIL's platform was designed with MSPs in mind. Add unlimited client tenants,
                            switch between them instantly, and get a consolidated view of threats across
                            your entire portfolio — without ever leaving the platform.
                        </Typography>
                        <Typography variant="body1" color="grey.700" mb={4} sx={{ fontSize: '1.1em', lineHeight: 1.8 }}>
                            Adding a new tenant takes less than 3 minutes. Your client grants access via
                            a secure Azure enterprise application, and PREVAIL immediately begins collecting
                            data and building behavioral baselines.
                        </Typography>
                        {[
                            { icon: <SwapHorizIcon sx={{ fontSize: 20, color: '#d90000' }} />, text: 'Instantly switch between client tenants' },
                            { icon: <DashboardIcon sx={{ fontSize: 20, color: '#d90000' }} />, text: 'Per-tenant alerts, activity logs, and reports' },
                            { icon: <PeopleAltIcon sx={{ fontSize: 20, color: '#d90000' }} />, text: 'Per-user behavioral profiles across all tenants' },
                            { icon: <AssessmentIcon sx={{ fontSize: 20, color: '#d90000' }} />, text: 'Custom reporting for client-facing deliverables' },
                            { icon: <NotificationImportantIcon sx={{ fontSize: 20, color: '#d90000' }} />, text: 'Configurable alerts per tenant — enable or disable with one click' },
                        ].map(({ icon, text }) => (
                            <Box key={text} display="flex" alignItems="flex-start" gap={1.5} mb={2}>
                                {icon}
                                <Typography variant="body2" color="grey.700" sx={{ fontSize: '1em' }}>{text}</Typography>
                            </Box>
                        ))}
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box
                            component="img"
                            src={fullDashboard}
                            sx={{ width: '100%', height: 'auto', display: 'block' }}
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* ─── PLATFORM DETAIL ─── */}
            <Box
                sx={{
                    background: '#242331',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={6} justifyContent="center" alignItems="center">
                    <Grid item xs={12} textAlign="center" mb={2}>
                        <Typography
                            variant="overline"
                            color="prevailRed.main"
                            fontWeight="bold"
                            sx={{ letterSpacing: 3, fontSize: '0.9em' }}
                        >
                            WHAT YOU CAN SEE
                        </Typography>
                        <Typography variant="h3" color="grey.100" fontWeight="bold" mt={1}>
                            Complete Visibility Across Every Tenant
                        </Typography>
                    </Grid>

                    
                    <Grid item xs={12} md={6}>
                        <Typography variant="h5" color="grey.200" fontWeight="bold" mb={3}>
                            Per-User Analytics for Every Client
                        </Typography>
                        <Typography variant="body1" color="grey.400" mb={3} lineHeight={1.8} sx={{ fontSize: '1.05em' }}>
                            PREVAIL builds a behavioral profile for every user in every tenant. You can
                            see exactly how each person is using Microsoft 365 — their sign-in patterns,
                            file access, sharing behavior, and application usage.
                        </Typography>
                        <Typography variant="body1" color="grey.400" lineHeight={1.8} sx={{ fontSize: '1.05em' }}>
                            When a profile deviates from the norm — a user downloading hundreds of files
                            at 2am, or accessing SharePoint from a new country — PREVAIL flags it
                            immediately. You get the alert. You take the action.
                        </Typography>
                        <Box mt={3}>
                            {[
                                'Sign-in location and device analytics',
                                'File access, download, and sharing history',
                                'Application usage patterns per user',
                                'Anomaly scores and threat confidence levels',
                                'Exportable reports for client reviews',
                            ].map((f) => (
                                <Box key={f} display="flex" alignItems="center" gap={1.5} mb={1.5}>
                                    <CheckCircleIcon sx={{ color: '#47A025', fontSize: 18, flexShrink: 0 }} />
                                    <Typography variant="body2" color="grey.300" sx={{ fontSize: '1em' }}>{f}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* ─── WHAT PREVAIL DETECTS ─── */}
            <Box
                sx={{
                    background: '#f8f9fa',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={4} justifyContent="center">
                    <Grid item xs={12} textAlign="center" mb={4}>
                        <Typography variant="h3" color="grey.800" fontWeight="bold">
                            What PREVAIL Detects for Your Clients
                        </Typography>
                        <Typography variant="h6" color="grey.600" mt={2} sx={{ maxWidth: '680px', mx: 'auto' }}>
                            PREVAIL doesn't rely on known attack signatures. Behavioral AI means it catches
                            novel threats your clients' built-in Microsoft security will miss.
                        </Typography>
                    </Grid>

                    {[
                        {
                            title: 'Successful Phishing Attacks',
                            detail: 'Detects when a user\'s credentials have been used by an unauthorized party, even when multi-factor authentication has been bypassed via MFA exhaustion or transparent proxy attacks.',
                        },
                        {
                            title: 'Account Takeover',
                            detail: 'Identifies unauthorized access through behavioral profiling — catching attackers who move slowly and carefully to avoid traditional rule-based detection.',
                        },
                        {
                            title: 'Data Exfiltration',
                            detail: 'Catches unusual file downloads, mass SharePoint access, and suspicious external sharing before sensitive client or business data leaves the organization.',
                        },
                        {
                            title: 'Insider Threats',
                            detail: 'Monitors for employees stealing data before resignation, sabotaging systems, or misusing elevated access — intentionally or through negligence.',
                        },
                        {
                            title: 'Supply Chain Compromise',
                            detail: 'Identifies when a compromised vendor or partner account is used to access your client\'s Microsoft 365 tenant or shared files.',
                        },
                        {
                            title: 'Ransomware Precursors',
                            detail: 'Catches the reconnaissance and credential harvesting activity that precedes a ransomware deployment — before encryption begins.',
                        },
                    ].map(({ title, detail }) => (
                        <Grid item size={{ xs: 12, md: 4 }}  key={title}>
                            <Box
                                sx={{
                                    border: '2px solid',
                                    borderColor: 'grey.200',
                                    borderRadius: 2,
                                    p: 3,
                                    height: '100%',
                                    transition: 'border-color 0.3s',
                                    '&:hover': { borderColor: '#d90000' },
                                }}
                            >
                                <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                                    <WarningIcon sx={{ color: '#DB9D47', fontSize: 22 }} />
                                    <Typography variant="h6" color="grey.800" fontWeight="bold">{title}</Typography>
                                </Box>
                                <Typography variant="body2" color="grey.600" lineHeight={1.8}>{detail}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* ─── PREVAIL vs TRADITIONAL SIEM ─── */}
            <Box
                sx={{
                    background: '#ffffff',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={4} justifyContent="center">
                    <Grid item xs={12} textAlign="center" mb={4}>
                        <Typography variant="h3" color="grey.800" fontWeight="bold">
                            PREVAIL vs. Traditional SIEM
                        </Typography>
                        <Typography variant="h6" color="grey.600" mt={2} sx={{ maxWidth: '650px', mx: 'auto' }}>
                            Traditional security tools put the burden back on your team.
                            PREVAIL automates the analysis so your team only responds when it matters.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Card sx={{ border: '2px solid #e0e0e0', borderRadius: 2, height: '100%' }}>
                            <CardContent sx={{ p: 4 }}>
                                <Typography variant="h5" color="grey.700" fontWeight="bold" mb={3}>
                                    Traditional SIEM / Manual Monitoring
                                </Typography>
                                {[
                                    'Analysts manually review alerts — slow response times',
                                    'Alert fatigue leads to missed threats',
                                    'Rule-based detection misses novel attack techniques',
                                    'High cost to scale across many tenants',
                                    'Requires skilled security analysts to interpret data',
                                    'Inconsistent coverage across shifts and weekends',
                                ].map((point) => (
                                    <Box key={point} display="flex" alignItems="flex-start" gap={1.5} mb={2}>
                                        <CancelIcon sx={{ color: '#d90000', mt: 0.2, fontSize: 20, flexShrink: 0 }} />
                                        <Typography variant="body2" color="grey.600" sx={{ fontSize: '0.95em' }}>{point}</Typography>
                                    </Box>
                                ))}
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Card sx={{ border: '2px solid #d90000', borderRadius: 2, height: '100%' }}>
                            <CardContent sx={{ p: 4 }}>
                                <Typography variant="h5" color="prevailRed.main" fontWeight="bold" mb={3}>
                                    PREVAIL for MSPs
                                </Typography>
                                {[
                                    'AI detects threats and alerts in under 1 minute',
                                    'Behavioral baselines eliminate alert noise',
                                    'Catches novel attacks with no prior signatures needed',
                                    'Scales to unlimited tenants at no extra complexity',
                                    'Actionable alerts — no analyst interpretation required',
                                    '24/7 automated monitoring with no gaps in coverage',
                                ].map((point) => (
                                    <Box key={point} display="flex" alignItems="flex-start" gap={1.5} mb={2}>
                                        <CheckCircleIcon sx={{ color: '#47A025', mt: 0.2, fontSize: 20, flexShrink: 0 }} />
                                        <Typography variant="body2" color="grey.700" sx={{ fontSize: '0.95em' }}>{point}</Typography>
                                    </Box>
                                ))}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* ─── TESTIMONIALS ─── */}
            <Box
                sx={{
                    background: '#242331',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={4} justifyContent="center">
                    <Grid item xs={12} textAlign="center" mb={4}>
                        <Typography variant="h3" color="grey.100" fontWeight="bold">
                            What MSPs Are Saying
                        </Typography>
                    </Grid>
                    {[
                        {
                            quote:
                                "The PREVAIL platform provides access to organized data and collaboration that we were unable to get elsewhere. We can provide evidence of exfiltration or destruction of data when employees give notice, and get notified immediately if a client account is compromised.",
                            name: "Principal",
                            company: "Managed IT Services Provider",
                        },
                        {
                            quote:
                                "PREVAIL provides a simple way to secure our customers and report on individual activity, easily switching between tenants. It takes less than a minute to add a tenant to the platform, with plenty of predefined alerts.",
                            name: "Principal",
                            company: "Managed IT Services",
                        },
                        {
                            quote:
                                "Prior to enabling 2-step authentication, someone got into a client's account early on a Sunday morning. Within minutes, PREVAIL notified us and we locked the account before any research data was accessed.",
                            name: "Senior Vice President",
                            company: "Medical Research Organization",
                        },
                    ].map(({ quote, name, company }, i) => (
                        <Grid item xs={12} md={4} key={i}>
                            <Card
                                sx={{
                                    background: '#3d0000',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    borderRadius: 2,
                                    height: '100%',
                                }}
                            >
                                <CardContent sx={{ p: 4 }}>
                                    <Typography
                                        color="prevailRed.main"
                                        sx={{ lineHeight: 0.8, mb: 2, fontSize: '3.5em', fontWeight: 900 }}
                                    >
                                        "
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        color="grey.300"
                                        mb={3}
                                        lineHeight={1.8}
                                        sx={{ fontStyle: 'italic' }}
                                    >
                                        {quote}
                                    </Typography>
                                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', mb: 2 }} />
                                    <Typography variant="body2" color="grey.200" fontWeight="bold">{name}</Typography>
                                    <Typography variant="caption" color="grey.500">{company}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* ─── PARTNER PRICING ─── */}
            <Box
                sx={{
                    background: '#ffffff',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={4} justifyContent="center" textAlign="center">
                    <Grid item size={{ xs: 12 }}>
                        <Typography variant="h3" color="grey.800" fontWeight="bold">
                            Partner Pricing Built for Scale
                        </Typography>
                        <Typography variant="h6" color="grey.600" mt={2} mb={2} sx={{ maxWidth: '650px', mx: 'auto' }}>
                            MSP partners receive up to 30% off retail pricing. The more clients you protect,
                            the better your margins.
                        </Typography>
                    </Grid>

                    <Grid item size={{ xs: 12, md: 5}} >
                        <Card sx={{ border: '2px solid #d90000', borderRadius: 2, height: '100%' }}>
                            <CardContent sx={{ p: 4 }}>
                                <Box
                                    sx={{
                                        background: '#d90000',
                                        color: 'white',
                                        borderRadius: 1,
                                        px: 1.5,
                                        py: 0.5,
                                        display: 'inline-block',
                                        mb: 2,
                                    }}
                                >
                                    <Typography variant="caption" color="grey.100" fontWeight="bold">MSP PARTNER PLAN</Typography>
                                </Box>
                                <Typography variant="h5" color="prevailRed.main" fontWeight="bold" mb={1}>
                                    MSP
                                </Typography>
                                <Typography variant="body1" color="grey.600" mb={2}>Contact us for partner pricing</Typography>
                                <Divider sx={{ my: 2 }} />
                                {[
                                    'Unlimited client tenants',
                                    'Unlimited admin users',
                                    'Up to 30% discount on retail pricing',
                                    'Monthly or yearly commitment options',
                                    'Custom data and metric retention',
                                    'Custom reporting for client deliverables',
                                    'Free integrations',
                                    'Priority email & phone support',
                                    'Early access to new features',
                                ].map((f) => (
                                    <Box key={f} display="flex" alignItems="center" gap={1} mb={1.5} textAlign="left">
                                        <CheckCircleIcon sx={{ color: '#47A025', fontSize: 18, flexShrink: 0 }} />
                                        <Typography variant="body2" color="grey.600">{f}</Typography>
                                    </Box>
                                ))}
                                <Box mt={3}>
                                    <Link to="/contact-us">
                                        <Button variant="contained" color="prevailRed" size="large" fullWidth>
                                            <Typography color="grey.100">Contact Us for Partner Pricing</Typography>
                                        </Button>
                                    </Link>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <Card sx={{ border: '2px solid #e0e0e0', borderRadius: 2, height: '100%' }}>
                            <CardContent sx={{ p: 4 }}>
                                <Typography variant="h5" color="grey.700" fontWeight="bold" mb={1}>
                                    Try It First
                                </Typography>
                                <Typography variant="body1" color="grey.600" mb={2}>
                                    30-day free trial — up to 3 Microsoft 365 tenants
                                </Typography>
                                <Divider sx={{ my: 2 }} />
                                {[
                                    'Full platform access for 30 days',
                                    'Up to 3 client tenants included',
                                    'All detection capabilities enabled',
                                    'Real-time alerting and reporting',
                                    'No credit card required',
                                    'No commitment — cancel anytime',
                                ].map((f) => (
                                    <Box key={f} display="flex" alignItems="center" gap={1} mb={1.5} textAlign="left">
                                        <CheckCircleIcon sx={{ color: '#47A025', fontSize: 18, flexShrink: 0 }} />
                                        <Typography variant="body2" color="grey.600">{f}</Typography>
                                    </Box>
                                ))}
                                <Box mt={3}>
                                    <Link to="https://app.prevailcyber.com/sign-up" target="_blank">
                                        <Button variant="outlined" color="prevailRed" size="large" fullWidth>
                                            <Typography>Start Free Trial</Typography>
                                        </Button>
                                    </Link>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* ─── FINAL CTA ─── */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #2D0000 0%, #6b0000 60%, #242331 100%)',
                    py: 12,
                    display: 'grid',
                    placeItems: 'center',
                    textAlign: 'center',
                }}
            >
                <Grid container maxWidth="md" spacing={2} justifyContent="center">
                    <Grid item xs={12}>
                        <Typography variant="h3" color="grey.100" fontWeight="bold" mb={2}>
                            Ready to Add AI Security to Your Stack?
                        </Typography>
                        <Typography variant="h6" color="grey.400" mb={5} sx={{ maxWidth: '580px', mx: 'auto' }}>
                            Start your 30-day free trial with up to 3 tenants — or contact us to
                            discuss partner pricing and a custom demo.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Link to="https://app.prevailcyber.com/sign-up" target="_blank">
                                <Button
                                    variant="contained"
                                    color="prevailRed"
                                    size="large"
                                    sx={{ pl: 6, pr: 6, py: 2 }}
                                >
                                    <Typography sx={{ fontSize: '1.2em' }} color="grey.100">
                                        Start 30-Day Trial
                                    </Typography>
                                </Button>
                            </Link>
                            <Link to="/contact-us">
                                <Button
                                    variant="outlined"
                                    size="large"
                                    sx={{ pl: 5, pr: 5, py: 2, borderColor: 'grey.500', color: 'grey.300' }}
                                >
                                    <Typography sx={{ fontSize: '1.2em' }}>Contact Us</Typography>
                                </Button>
                            </Link>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
}
